import throttle from "lodash/throttle";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useState } from "react";

export const useSuggestionMutation = () => {
  "use no memo";
  const [search, setSearch] = useState({ value: "", hasOptions: false });
  const debouncedSetSearch = debounce(setSearch, 500);
  const [node, setNode] = useState<HTMLDivElement>();
  const ref = useCallback((node: HTMLDivElement) => {
    setNode(node);
  }, []);

  const handleSearch = useCallback(
    throttle((editor: Element, hasList) => {
      if (hasList) return setSearch({ value: "", hasOptions: true });
      const text = editor.textContent || "";
      const words = text.trim().split(/\s+/);
      const lastWord = words[words.length - 1] || "";

      if (lastWord.startsWith("@")) {
        const search = lastWord.substring(1);
        debouncedSetSearch({ value: search, hasOptions: !search });
      } else {
        debouncedSetSearch({ value: "", hasOptions: true });
      }
    }, 100),
    [],
  );

  const handleMutation = useCallback(
    throttle((container: HTMLDivElement | null) => {
      if (!container) return;
      const textEditorWidthSuggestions = container.querySelector(
        '.lb-composer-editor[data-focused="true"][aria-controls]',
      );
      if (!textEditorWidthSuggestions)
        return debouncedSetSearch({ value: "", hasOptions: false });
      const hasActiveSuggestionList = container.querySelector(
        "[aria-activedescendant]",
      );
      handleSearch(textEditorWidthSuggestions, !!hasActiveSuggestionList);
    }, 200),
    [],
  );

  useEffect(() => {
    if (!node) return;

    const observer = new MutationObserver(() => handleMutation(node));
    observer.observe(node, {
      subtree: true,
      attributes: true,
      characterData: true,
      characterDataOldValue: true,
    });

    return () => {
      observer.disconnect();
      setSearch({ value: "", hasOptions: false });
    };
  }, [handleMutation, node]);

  return { ref, search };
};
